import React from 'react';
import { Box } from '@mui/material';
import { IconButton, Tooltip, Typography } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { AddRounded, Graph2Rounded } from '@mui-symbols-material/w300';
import { InstalledBaseNodeList } from '../../graphql/queries/installedBaseNodeList';
import { HeroListCard, HeroListRow, ViewAllLink } from './heroPrimitives';
import { SkeletonListRows, fadeInSx } from './HeroSkeletons';
import { useSubAssetCreation } from '../../hooks/useSubAssetCreation';
import { buildInstalledBasePathname } from '../../utils/installedBaseUrl';
import { getPathNodesAbove } from '../../utils/installedBasePath';

interface Props {
    installedBaseNodeId: string;
    compact?: boolean;
}

/**
 * Lists the child assets directly under this installed-base node. Rows open the sub-asset's
 * dashboard; "view all" opens the tree with this asset's children column. Users who may
 * create sub-assets get a "+" in the header, and an empty state instead of a hidden card.
 */
const SubAssetsListWidget = ({ installedBaseNodeId, compact }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const limit = compact ? 3 : 5;
    const subAssetCreation = useSubAssetCreation(installedBaseNodeId);

    const { data, loading } = useQuery(InstalledBaseNodeList, {
        variables: {
            filter: { parentNodeId: { equals: installedBaseNodeId } },
            pagination: { start: 0, limit },
        },
        skip: !installedBaseNodeId,
    });

    const total = _.get(data, 'installedBaseNodeList.total', 0) ?? 0;
    const nodes: any[] = _.get(data, 'installedBaseNodeList.installedBaseNodes', []) ?? [];

    // Without sub-assets the card only earns its place when the user can create one.
    if (!loading && total === 0 && !subAssetCreation.canOffer) return null;

    const goToInstalledBase = () => {
        const ancestorIds = getPathNodesAbove(subAssetCreation.node)
            .map((n) => n.id)
            .filter((id): id is string => !!id);
        // Before the node (and its path) is loaded the tree repairs a bare id on its own.
        navigate(buildInstalledBasePathname([null, ...ancestorIds, installedBaseNodeId]));
    };

    const createLabel = t('Create Sub-Asset');
    const createTooltip = subAssetCreation.disabledReason ?? createLabel;

    const headerAction = subAssetCreation.canOffer && total > 0 ? (
        <Tooltip title={createTooltip} size="sm">
            {/* span: a disabled button fires no hover events, so the tooltip needs a wrapper */}
            <span>
                <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    aria-label={createLabel}
                    disabled={subAssetCreation.disabled}
                    onClick={subAssetCreation.open}
                >
                    <AddRounded />
                </IconButton>
            </span>
        </Tooltip>
    ) : undefined;

    return (
        <>
            <HeroListCard
                title={t('Sub-assets')}
                icon={<Graph2Rounded />}
                count={total}
                headerAction={headerAction}
                dense={compact}
                viewAll={total > 0 ? `${t('View all sub-assets')} (${total})` : undefined}
                onViewAll={goToInstalledBase}
            >
                {loading && !data ? (
                    <SkeletonListRows rows={compact ? 2 : 3} />
                ) : total === 0 ? (
                    <Box>
                        <Typography level="bodySmall" sx={{ color: '#777' }}>
                            {t('No sub-assets yet')}
                        </Typography>
                        <Tooltip title={subAssetCreation.disabledReason ?? ''} size="sm">
                            <span>
                                <ViewAllLink
                                    label={createLabel}
                                    icon={<AddRounded />}
                                    disabled={subAssetCreation.disabled}
                                    onClick={subAssetCreation.open}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box sx={fadeInSx}>
                        {nodes.map((node) => {
                            const assetLocationId: string | undefined = _.get(node, 'assetLocation.id');
                            return (
                                <HeroListRow
                                    key={node.id}
                                    icon={<Graph2Rounded />}
                                    title={node.title}
                                    meta={_.get(node, 'assetLocation.asset.serialNumber') || undefined}
                                    onClick={assetLocationId
                                        ? () => navigate(`/assets/${assetLocationId}/dashboard`)
                                        : undefined}
                                />
                            );
                        })}
                    </Box>
                )}
            </HeroListCard>
            {subAssetCreation.modal}
        </>
    );
};

export default SubAssetsListWidget;
